import {
  ListItemText,
  Theme,
  Typography,
  withStyles,
  WithStyles
} from '@material-ui/core'
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import { ToggleButtonGroup } from '@material-ui/lab'
import ToggleButton from '@material-ui/lab/ToggleButton'
import { FC, useState, useEffect, MouseEvent, useCallback } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { selectApplications } from '../../../actions/applications'
import { SelectApplicationsArray } from '../../../actions/types'
import {
  selectAvailabilitiesByApartment,
  selectAvailabilitiesByApplicationIds
} from '../../../selectors/applicantAvailability'

import {
  getApplicationsNameIdPairs,
  selectSelectedApplications
} from '../../../selectors/applications'

import { selectContentIdFromPathname } from '../../../selectors/router'
import { ApplicantAvailability } from '../../../types/applicantAvailability'
import { ApplicationsByStatus } from '../../../types/applications'
import { StateInterface } from '../../../types/states'
import {
  getApplicationIdsFromApplicantAvailabilities,
  getAvailabilitesOnSpesificTime,
  getAvailabilitiesOnSpesificDate
} from '../../../utils/applicantAvailabilities'
import { getApplicationNameFromId } from '../../../utils/applications'

const styles = ({ palette }: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '24px'
  },
  subContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  titleContainer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    marginTop: '8px'
  },
  title: {
    display: 'flex',
    'text-transform': 'uppercase',
    fontSize: '0.9rem',
    color: palette.grey[300]
  },
  titleLine: {
    height: '1px',
    borderBottom: '1px solid ' + palette.grey[300],
    'flex-grow': '1',
    marginLeft: '8px'
  },
  toggleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '8px 0',
    background: 'none !important',
    border: 'none'
  },
  name: {
    textAlign: 'left' as const,
    color: '#000 !important',
    'text-transform': 'capitalize'
  },
  tagArea: {
    display: 'flex',
    'flex-direction': 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 'auto'
  },
  availabilityTag: {
    border: '1px solid #02CD98',
    borderRadius: '4px',
    padding: '4px',
    marginLeft: 'auto'
  },
  tagTitle: {
    color: '#02CD98',
    fontSize: '0.5rem'
  },
  invitedTag: {
    background: '#f8fc9d',
    padding: '4px',
    display: 'block',
    borderRadius: '4px',
    marginLeft: '5px',
    fontSize: '0.5rem',
    border: '1px solid #f8fc9d'
  },
  invitedTagTitle: {
    fontSize: '0.5rem'
  }
})

interface ApplicantsToggleGroupProps {
  applicationsByStatus?: ApplicationsByStatus
  confirmedApplications: string[]
  invitedApplications?: string[]
  showingDateTime: string
}

interface StateProps {
  applicationNameIdPairs?: any[]
  selectedApplications: string[]
  availabilities?: ApplicantAvailability[]
}

interface DispatchProps {
  selectApplicationsAction: SelectApplicationsArray
}

type ComponentProps = ApplicantsToggleGroupProps &
  StateProps &
  DispatchProps &
  WithStyles &
  WithTranslation

const ApplicantsToggleGroup: FC<ComponentProps> = ({
  applicationsByStatus,
  confirmedApplications,
  classes,
  t,
  applicationNameIdPairs,
  selectedApplications,
  selectApplicationsAction,
  availabilities,
  showingDateTime,
  invitedApplications
}) => {
  const [selected, setSelected] = useState<string[]>([])

  const notAvailableApplications = (): string[] => {
    const applications = applicationsByStatus?.created
      .concat(applicationsByStatus?.noAvailableTimes)
      .map((application) => application.applicationId)
      .filter((id) => invitedApplications?.includes(id))

    return applications ? applications : []
  }

  const pendingInvitedApplications = () => {
    const notAvailableIds = notAvailableApplications()
    const applications = invitedApplications?.filter(
      (id) =>
        !notAvailableIds.includes(id) && !confirmedApplications.includes(id)
    )

    return applications ? applications : []
  }

  const invitableApplicants = (): string[] => {
    const applications = applicationsByStatus?.created
      .concat(applicationsByStatus?.noAvailableTimes)
      .concat(applicationsByStatus?.accepted)
      .map((application) => application.applicationId)
      .filter(
        (id) => !selected.includes(id) && !invitedApplications?.includes(id)
      )

    return applications ? applications : []
  }

  const availableApplicants = () => {
    if (!availabilities) {
      return []
    }

    const ids = invitedApplications
      ? [...invitableApplicants(), ...selected, ...invitedApplications]
      : [...invitableApplicants(), ...selected]

    const matchingAvailabilities = selectAvailabilitiesByApplicationIds(
      availabilities,
      ids
    )
    const availabilitiesOnSelectedDate = getAvailabilitiesOnSpesificDate(
      matchingAvailabilities,
      showingDateTime
    )
    const availabilitiesOnSelectedTime = getAvailabilitesOnSpesificTime(
      availabilitiesOnSelectedDate,
      showingDateTime
    )
    return getApplicationIdsFromApplicantAvailabilities(
      availabilitiesOnSelectedTime
    )
  }

  const availabilityTag = (id: string) => {
    if (!availableApplicants().includes(id)) {
      return null
    }

    return (
      <div className={classes.availabilityTag}>
        <Typography className={classes.tagTitle} variant='subtitle2'>
          {t(`showings.createShowings.toggleGroup.available`)}
        </Typography>
      </div>
    )
  }
  const getApplicantName = (id: string) =>
    getApplicationNameFromId(applicationNameIdPairs, id)

  const handleSelectedChange = (
    event: MouseEvent<HTMLElement>,
    newSelected: string[]
  ) => {
    setSelected(newSelected)
    selectApplicationsAction(newSelected)
  }

  /** Check that invitedApplications aren´t selected */
  useEffect(() => {
    const initialSelected = selectedApplications.filter(
      (application) => !invitedApplications?.includes(application)
    )

    setSelected(initialSelected)
    selectApplicationsAction(initialSelected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isApplicantReserved = useCallback(
    (id: string) => {
      return applicationsByStatus?.showingReserved.some(
        (application) => application.applicationId === id
      )
    },
    [applicationsByStatus]
  )
  /**
   *
   * NOTE: To make ToggleButtonGroup work, ToggleButton´s needs to be direct children of ToggleButtonGroup
   *
   */

  return (
    <ToggleButtonGroup
      className={classes.container}
      value={selected}
      onChange={handleSelectedChange}
    >
      <div className={classes.subContainer}>
        <ListItemText secondary={`${t(`Valitut`)} (${selected.length})`} />
      </div>

      {/* Confirmed applicants */}
      {confirmedApplications.length > 0 && (
        <div className={classes.titleContainer}>
          <Typography className={classes.title} variant='subtitle2'>
            {t(`showings.createShowings.toggleGroup.confirmed`)}
          </Typography>
          <div className={classes.titleLine} />
        </div>
      )}
      {confirmedApplications.map((id) => (
        <ToggleButton
          key={id}
          className={classes.toggleButton}
          disabled
          value={id}
        >
          <Typography className={classes.name} variant='subtitle2'>
            {getApplicantName(id)}
          </Typography>
        </ToggleButton>
      ))}

      {/* Invited applicants */}
      {pendingInvitedApplications() &&
        pendingInvitedApplications().length > 0 &&
        pendingInvitedApplications().some((id) => !isApplicantReserved(id)) && (
          <div className={classes.titleContainer}>
            <Typography className={classes.title} variant='subtitle2'>
              {t(`showings.createShowings.toggleGroup.waiting`)}
            </Typography>
            <div className={classes.titleLine} />
          </div>
        )}
      {pendingInvitedApplications()?.map(
        (id) =>
          !isApplicantReserved(id) && (
            <ToggleButton
              key={id}
              className={classes.toggleButton}
              disabled
              value={id}
            >
              <Typography className={classes.name} variant='subtitle2'>
                {getApplicantName(id)}
              </Typography>
              {availabilityTag(id)}
            </ToggleButton>
          )
      )}

      {/* Selected applicants */}
      {selected.length > 0 && (
        <div className={classes.titleContainer}>
          <Typography className={classes.title} variant='subtitle2'>
            {t(`showings.createShowings.toggleGroup.selected`)}
          </Typography>
          <div className={classes.titleLine} />
        </div>
      )}
      {selected.map((id) => (
        <ToggleButton
          key={id}
          className={classes.toggleButton}
          // selected
          value={id}
        >
          <Typography className={classes.name} variant='subtitle2'>
            {getApplicantName(id)}
          </Typography>
          {availabilityTag(id)}
          <CheckCircleRoundedIcon />
        </ToggleButton>
      ))}

      {/* Invitable applications */}
      {invitableApplicants().length > 0 && (
        <div className={classes.titleContainer}>
          <Typography className={classes.title} variant='subtitle2'>
            {t(`showings.createShowings.toggleGroup.invitable`)}
          </Typography>
          <div className={classes.titleLine} />
        </div>
      )}
      {invitableApplicants().map((id) => (
        <ToggleButton key={id} className={classes.toggleButton} value={id}>
          <Typography className={classes.name} variant='subtitle2'>
            {getApplicantName(id)}
          </Typography>
          <span className={classes.tagArea}>
            {availabilityTag(id)}
            {applicationsByStatus?.accepted.find(
              (element) => element.applicationId === id
            ) && (
              <div className={classes.invitedTag}>
                <Typography
                  variant='subtitle2'
                  className={classes.invitedTagTitle}
                >
                  {t(`showings.createShowings.invited`)}
                </Typography>
              </div>
            )}
          </span>
          <RadioButtonUncheckedIcon />
        </ToggleButton>
      ))}

      {/* notAvailableTime applicants */}
      {notAvailableApplications() && notAvailableApplications().length > 0 && (
        <div className={classes.titleContainer}>
          <Typography className={classes.title} variant='subtitle2'>
            {t(`showings.createShowings.toggleGroup.notAvailable`)}
          </Typography>
          <div className={classes.titleLine} />
        </div>
      )}
      {notAvailableApplications()?.map((id) => (
        <ToggleButton
          key={id}
          className={classes.toggleButton}
          disabled
          value={id}
        >
          <Typography className={classes.name} variant='subtitle2'>
            {getApplicantName(id)}
          </Typography>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)

  return {
    applicationNameIdPairs: apartmentId
      ? getApplicationsNameIdPairs(apartmentId, state)
      : undefined,
    selectedApplications: selectSelectedApplications(state),
    availabilities: apartmentId
      ? selectAvailabilitiesByApartment(apartmentId)(state)
      : undefined
  }
}
const mapDispatchToProps = (dispatch: Dispatch) => ({
  selectApplicationsAction: (applicationIdArray: string[]) =>
    dispatch(selectApplications(applicationIdArray))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withStyles(styles)(ApplicantsToggleGroup)))
